import { useState } from 'react';

import { CalendarDays, Check, Clock, Plus, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  useBookingsByCall,
  useCreateBooking,
  useDeleteBooking,
  useSetAttendance,
} from '@/hooks/useBookings';
import { bookingStatusLabel, bookingStatusTone } from '@/lib/booking-status';

import type { BookingWithRelations } from '@repo/shared-types';

interface CallBookingsSectionProps {
  callId: string;
}

interface BookingNoteFields {
  customerName: string;
  dealership: string;
  registrationNumber: string;
  phoneNumber: string;
  paymentType: string;
}

const buildBookingNote = (fields: BookingNoteFields): string | null => {
  const lines: string[] = [];
  if (fields.customerName) lines.push(`Customer: ${fields.customerName}`);
  if (fields.dealership) lines.push(`Dealership: ${fields.dealership}`);
  if (fields.registrationNumber) lines.push(`Reg: ${fields.registrationNumber}`);
  if (fields.phoneNumber) lines.push(`Phone: ${fields.phoneNumber}`);
  if (fields.paymentType) lines.push(`Payment: ${fields.paymentType}`);
  return lines.length > 0 ? lines.join('\n') : null;
};

const AddBookingDialog = ({
  open,
  onOpenChange,
  callId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callId: string;
}) => {
  const { toast } = useToast();
  const createBooking = useCreateBooking();

  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [dealership, setDealership] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [paymentType, setPaymentType] = useState('');

  const resetForm = () => {
    setDate('');
    setTime('');
    setCustomerName('');
    setDealership('');
    setRegistrationNumber('');
    setPhoneNumber('');
    setPaymentType('');
  };

  const handleSubmit = async () => {
    if (!date) {
      toast({ title: 'Date is required', variant: 'destructive' });
      return;
    }

    const hasTime = time !== '';
    const dateTime = `${date}T${hasTime ? time : '00:00'}:00`;
    const description = buildBookingNote({
      customerName,
      dealership,
      registrationNumber,
      phoneNumber,
      paymentType,
    });

    const title = customerName ? `Booking - ${customerName}` : 'Manual Booking';

    try {
      await createBooking.mutateAsync({
        date: dateTime,
        title,
        description,
        has_time: hasTime,
        call_id: callId,
      });
      toast({ title: 'Booking created' });
      resetForm();
      onOpenChange(false);
    } catch {
      toast({ title: 'Failed to create booking', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-base">Add Booking</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-3">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-muted-foreground mb-1.5 flex items-center gap-1.5 text-xs font-medium">
                <CalendarDays className="h-3 w-3" />
                Date *
              </label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
            <div className="w-36">
              <label className="text-muted-foreground mb-1.5 flex items-center gap-1.5 text-xs font-medium">
                <Clock className="h-3 w-3" />
                Time
              </label>
              <div className="relative">
                <Input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="h-9 pr-7 text-sm"
                />
                {time && (
                  <button
                    type="button"
                    onClick={() => setTime('')}
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-1.5 -translate-y-1/2"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
                Customer Name
              </label>
              <Input
                placeholder="e.g. John Smith"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
            <div>
              <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
                Dealership
              </label>
              <Input
                placeholder="e.g. V12 Sports"
                value={dealership}
                onChange={(e) => setDealership(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
                Registration Number
              </label>
              <Input
                placeholder="e.g. AB12 CDE"
                value={registrationNumber}
                onChange={(e) => setRegistrationNumber(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
            <div>
              <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
                Phone Number
              </label>
              <Input
                placeholder="e.g. 07700 900000"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
              Cash or Finance
            </label>
            <Select value={paymentType} onValueChange={setPaymentType}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Select payment type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Cash">Cash</SelectItem>
                <SelectItem value="Finance">Finance</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={createBooking.isPending}>
            {createBooking.isPending ? 'Saving...' : 'Create Booking'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BookingCard = ({ booking }: { booking: BookingWithRelations }) => {
  const { toast } = useToast();
  const deleteBooking = useDeleteBooking();
  const setAttendance = useSetAttendance();

  const statusTone = bookingStatusTone(booking.status);
  const isCancelled = booking.status === 'cancelled';
  const isActive = booking.status === 'active';
  const attendance = (booking as BookingWithRelations & { attendance?: string | null }).attendance;
  const isAttended = attendance === 'attended';
  const isNotAttended = attendance === 'not_attended';
  const hasTime = (booking as BookingWithRelations & { has_time?: boolean }).has_time !== false;

  const bookingDate = new Date(booking.date);
  const formattedDate = bookingDate.toLocaleDateString('en-GB', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const formattedTime = bookingDate.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const handleAttendance = async (value: 'attended' | 'not_attended' | null) => {
    try {
      await setAttendance.mutateAsync({ id: booking.id, attendance: value });
      toast({ title: value ? `Marked as ${value.replace('_', ' ')}` : 'Attendance cleared' });
    } catch {
      toast({ title: 'Failed to update attendance', variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteBooking.mutateAsync(booking.id);
      toast({ title: 'Booking deleted' });
    } catch {
      toast({ title: 'Failed to delete booking', variant: 'destructive' });
    }
  };

  return (
    <div className="rounded-lg border p-4 dark:border-gray-700">
      <div className="flex items-start justify-between">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <span className={`font-medium ${statusTone.struck ? 'line-through opacity-60' : ''}`}>
              {booking.title}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                isActive && isAttended
                  ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                  : isActive && isNotAttended
                    ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                    : statusTone.chip
              }`}
            >
              {isActive && isAttended
                ? 'Attended'
                : isActive && isNotAttended
                  ? 'Not Attended'
                  : bookingStatusLabel(booking.status)}
            </span>
          </div>
          <div className="text-muted-foreground flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1">
              <CalendarDays className="h-3 w-3" />
              {formattedDate}
            </span>
            {hasTime && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formattedTime}
              </span>
            )}
          </div>
          {booking.description && (
            <p className="text-muted-foreground text-xs whitespace-pre-line">
              {booking.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!isCancelled && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className={`h-7 w-7 ${isAttended ? 'text-green-600' : ''}`}
                onClick={() => handleAttendance(isAttended ? null : 'attended')}
                disabled={setAttendance.isPending}
                title={isAttended ? 'Clear attendance' : 'Mark attended'}
              >
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={`h-7 w-7 ${isNotAttended ? 'text-red-600' : ''}`}
                onClick={() => handleAttendance(isNotAttended ? null : 'not_attended')}
                disabled={setAttendance.isPending}
                title={isNotAttended ? 'Clear attendance' : 'Mark not attended'}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive hover:text-destructive h-7 w-7"
            onClick={handleDelete}
            disabled={deleteBooking.isPending}
            title="Delete booking"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const CallBookingsSection = ({ callId }: CallBookingsSectionProps) => {
  const { data: bookingsResponse } = useBookingsByCall(callId);
  const [dialogOpen, setDialogOpen] = useState(false);

  const bookings = bookingsResponse?.data ?? [];

  return (
    <>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarDays className="text-muted-foreground h-5 w-5" />
              <CardTitle className="text-sm">Bookings</CardTitle>
              {bookings.length > 0 && (
                <span className="text-muted-foreground text-xs">({bookings.length})</span>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
              <Plus className="mr-1 h-3.5 w-3.5" />
              Add Booking
            </Button>
          </div>
          <CardDescription>Bookings associated with this call</CardDescription>
        </CardHeader>
        <CardContent>
          {bookings.length === 0 ? (
            <p className="text-muted-foreground py-4 text-center text-sm">No bookings yet</p>
          ) : (
            <div className="space-y-3">
              {bookings.map((booking) => (
                <BookingCard key={booking.id} booking={booking} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AddBookingDialog open={dialogOpen} onOpenChange={setDialogOpen} callId={callId} />
    </>
  );
};
